import { createContext, isValidElement, useCallback, useContext, useEffect, useId, useRef, useState, useSyncExternalStore, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ChevronDown, ChevronRight, Info, Languages, Menu, PanelLeftClose, PanelLeftOpen, Sparkles, X, type LucideIcon } from "lucide-react";
import uaeEmblem from "../assets/uae-emblem.svg";
import { cn } from "../lib/cn";
import { microcopy, useDocumentLocale, type UiLocale } from "../lib/microcopy";
import { useMediaQuery } from "../lib/media";
import type { Tone } from "../lib/tone";
import { BrandMark, BrandWordmark } from "./brand";
import { Button } from "./button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, Tooltip } from "./overlays";
import { MobileDrawer, MobileDrawerContent, MobileDrawerTitle } from "./mobile-drawer";

// ---------------------------------------------------------------- Language switch
/**
 * The UAE Design System's language link: the other language's name, written in that language ("العربية" on English pages, "English"
 * on Arabic ones), so a reader who cannot read the current language still finds it.
 */
export function LanguageSwitch({ locale, onChange, className }: { locale: UiLocale; onChange: (locale: UiLocale) => void; className?: string }) {
  const other: UiLocale = locale === "ar" ? "en" : "ar";
  return (
    <button
      type="button"
      onClick={() => onChange(other)}
      lang={other}
      title={microcopy("switchLanguage", locale)}
      className={cn(
        "inline-flex h-9 shrink-0 items-center gap-2 rounded-md px-2 text-sm font-medium text-aeblack-700 transition-colors duration-300 hover:text-aegold-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
        other === "ar" ? "font-arabic" : "font-sans",
        className,
      )}
    >
      <Languages aria-hidden className="size-4 text-aegold-600" />
      <span>{microcopy(other === "ar" ? "languageArabic" : "languageEnglish", other)}</span>
    </button>
  );
}

/** The emblem of the United Arab Emirates, which the Design System header carries at its top end. */
export function GovEmblem({ className }: { className?: string }) {
  const locale = useDocumentLocale();
  return <img src={uaeEmblem} alt={microcopy("uaeEmblem", locale)} className={cn("h-12 w-auto shrink-0", className)} />;
}

// ---------------------------------------------------------------- Federal gov bar (public portal)
export interface GovBarProps {
  ministryName: string;
  ministryNameSecondary: string;
  /** The product or app name under the logo (left out when the logo says it all). */
  platformName?: string;
  locale: "en" | "ar";
  onToggleLocale: () => void;
  /** Controls beside the language link (the Design System's "common links"). */
  right?: ReactNode;
  /** The main navigation, drawn as the Design System's navigation row under the logos (it scrolls sideways on narrow screens). */
  nav?: ReactNode;
  /** Official ministry logotype (bilingual PNG/SVG). When provided it replaces the platform logo and the government's name. */
  logo?: { src: string; alt: string };
  /** Shows the emblem of the United Arab Emirates at the header's top end, as federal entities' sites do. */
  emblem?: boolean;
  homeHref?: string;
  onHome?: () => void;
  className?: string;
}

/**
 * The UAE Design System 3.0 header (`aegov-header`): the 8px gold rule, the logos (the platform's logo, a divider and the owning
 * entity's name), the common links (language and any `right` controls) and the UAE emblem at the top end, then the navigation row
 * on AE Black 50 with bold items and the current one underlined in AE Gold 900.
 */
export function GovBar({ ministryName, ministryNameSecondary, platformName, locale, onToggleLocale, right, nav, logo, emblem = false, homeHref = "/", onHome, className }: GovBarProps) {
  return (
    <header data-print="hide" className={cn("relative bg-white", className)}>
      <span aria-hidden className="gov-strip block h-2" />
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6 lg:px-8 lg:py-4">
        <a
          href={homeHref}
          onClick={(e) => {
            if (onHome) {
              e.preventDefault();
              onHome();
            }
          }}
          className="flex min-w-0 items-center gap-4 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring lg:gap-6"
        >
          {logo ? (
            <img src={logo.src} alt={logo.alt} className="max-h-12 w-auto max-w-[8.75rem] shrink-0" />
          ) : (
            <>
              <span className="flex min-w-0 items-center gap-3">
                <BrandMark size={44} />
                <span className="flex min-w-0 flex-col gap-1.5 leading-tight">
                  <BrandWordmark size={19} />
                  {platformName ? <span className="hidden truncate text-xs font-medium text-aeblack-600 sm:block">{platformName}</span> : null}
                </span>
              </span>
              <span aria-hidden className="hidden h-10 w-px shrink-0 bg-aeblack-100 lg:block" />
              <span className="hidden min-w-0 flex-col gap-0.5 leading-tight lg:flex">
                <span className="truncate text-sm font-bold text-aeblack-800">{ministryName}</span>
                <span className="truncate text-xs text-aeblack-600">{ministryNameSecondary}</span>
              </span>
            </>
          )}
        </a>
        <div className="ms-auto flex shrink-0 items-center gap-1 sm:gap-3">
          <div className="flex items-center gap-1 sm:gap-2">
            {right}
            {right ? <span aria-hidden className="hidden h-4 w-px bg-aegold-300 sm:block" /> : null}
            <LanguageSwitch locale={locale} onChange={onToggleLocale} />
          </div>
          {emblem ? <GovEmblem className="hidden h-10 sm:block lg:h-12" /> : null}
        </div>
      </div>
      {nav ? (
        <div className="border-t border-aeblack-100 bg-aeblack-50">
          <div className="scrollbar-none mx-auto max-w-7xl overflow-x-auto px-1 sm:px-3 lg:px-5">{nav}</div>
        </div>
      ) : null}
    </header>
  );
}

/** One item of the Design System navigation row (`main-navigation`): bold AE Black 800, underlined in AE Gold 900 when current. */
export const govNavItemClass = (active: boolean) =>
  cn(
    "relative flex h-12 items-center whitespace-nowrap border-b-2 px-3 text-sm font-bold transition-colors duration-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring rtl:font-semibold",
    active ? "border-aegold-900 text-aegold-900" : "border-transparent text-aeblack-800 hover:border-aegold-500 hover:text-aegold-700",
  );

// ---------------------------------------------------------------- Side navigation
export interface NavItem {
  key: string;
  label: ReactNode;
  icon: LucideIcon;
  /** The colour of the item's icon tile. */
  tone?: Tone;
  href: string;
  badge?: ReactNode;
  active?: boolean;
  onClick?: () => void;
}

export interface NavSection {
  /** Remembers whether the reader folded the section; sections without one can't be folded. */
  key?: string;
  title?: ReactNode;
  /** Starts folded until the reader opens it, unless it holds the page that is open. */
  folded?: boolean;
  items: NavItem[];
}

const FOLDED_KEY = "pmo.nav.folded";

function readFolded(): Record<string, boolean> {
  try {
    const parsed = JSON.parse(localStorage.getItem(FOLDED_KEY) ?? "{}") as unknown;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

export function SideNav({ sections, footer, brand, className, collapsed = false, onToggleCollapsed }: {
  sections: NavSection[];
  footer?: ReactNode;
  /** The emblem and platform name above the menu (the mobile drawer); on desktop the shell header carries them. */
  brand?: ReactNode;
  className?: string;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}) {
  const locale = useDocumentLocale();
  const toggleLabel = microcopy(collapsed ? "expandSidebar" : "collapseSidebar", locale);
  // Tooltips open away from the rail: to the right in LTR, to the left when the document runs right-to-left.
  const tipSide = locale === "ar" ? "left" : "right";
  const [folded, setFolded] = useState(readFolded);
  const fold = (key: string, next: boolean) =>
    setFolded((f) => {
      const all = { ...f, [key]: next };
      try {
        localStorage.setItem(FOLDED_KEY, JSON.stringify(all));
      } catch {
        /* storage unavailable */
      }
      return all;
    });
  return (
    <nav className={cn("flex h-full flex-col", className)} aria-label={microcopy("mainMenu", locale)}>
      {brand ? <div className={cn("flex h-[var(--topbar-h)] shrink-0 items-center", collapsed ? "justify-center px-2" : "px-4")}>{brand}</div> : null}
      <div className={cn("flex min-h-0 flex-1 flex-col overflow-y-auto scrollbar-thin py-4", collapsed ? "gap-3 px-2.5" : "gap-6 ps-5 pe-3")}>
        {sections.map((section, si) => {
          const foldable = !!section.key && !!section.title && !collapsed;
          const isFolded = foldable && (folded[section.key!] ?? (!!section.folded && !section.items.some((i) => i.active)));
          const listId = `nav-section-${section.key ?? si}`;
          return (
            <div key={section.key ?? si}>
              {collapsed ? (
                si > 0 ? <div className="mx-auto mb-3 h-px w-5 bg-aeblack-100" /> : null
              ) : foldable ? (
                <button
                  type="button"
                  aria-expanded={!isFolded}
                  aria-controls={listId}
                  onClick={() => fold(section.key!, !isFolded)}
                  className="group/section mb-2 flex w-full items-center gap-1 rounded-sm py-0.5 text-start font-heading text-[15px] font-bold text-aeblack-800 outline-none transition-colors hover:text-aegold-700 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
                >
                  <span className="truncate">{section.title}</span>
                  <ChevronDown aria-hidden className={cn("ms-auto size-4 shrink-0 text-aeblack-500 transition-[transform,opacity] duration-200", isFolded ? "-rotate-90 rtl:rotate-90" : "opacity-0 group-hover/section:opacity-100 group-focus-visible/section:opacity-100")} />
                </button>
              ) : section.title ? (
                <p className="mb-2 py-0.5 font-heading text-[15px] font-bold text-aeblack-800">{section.title}</p>
              ) : null}
              <div id={listId} inert={isFolded || undefined} className={cn("grid transition-[grid-template-rows,opacity] duration-300 ease-out-expo", isFolded ? "grid-rows-[0fr] opacity-0" : "grid-rows-[1fr]")}>
                <ul className={cn("flex min-h-0 flex-col overflow-hidden", collapsed ? "gap-px" : "gap-0.5")}>
                  {section.items.map((item) => {
                    const link = (
                      <a
                        href={item.href}
                        onClick={(e) => {
                          if (item.onClick) {
                            e.preventDefault();
                            item.onClick();
                          }
                        }}
                        aria-current={item.active ? "page" : undefined}
                        className={cn(
                          "group relative flex items-center gap-2.5 text-sm outline-none transition-colors duration-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
                          collapsed
                            ? cn("mx-auto size-10 justify-center rounded-md", item.active ? "bg-aegold-100 text-aegold-900" : "text-aeblack-800 hover:bg-white hover:text-aegold-700")
                            : cn("min-h-9 border-s-2 py-1.5 ps-3 pe-2", item.active ? "border-aegold-600 font-medium text-aegold-700" : "border-transparent text-aeblack-800 hover:border-aegold-300 hover:text-aegold-700"),
                        )}
                      >
                        <item.icon aria-hidden strokeWidth={1.75} className={cn("shrink-0 transition-colors", collapsed ? "size-[19px]" : "size-4", item.active ? "text-aegold-700" : "text-aeblack-500 group-hover:text-aegold-600")} />
                        {collapsed ? (
                          item.badge ? <span className="absolute top-1 end-1 size-2 rounded-full bg-aered-600 ring-2 ring-canvas" aria-hidden /> : null
                        ) : (
                          <>
                            <span className="truncate">{item.label}</span>
                            {item.badge ? <span className="ms-auto">{item.badge}</span> : null}
                          </>
                        )}
                      </a>
                    );
                    return (
                      <li key={item.key}>
                        {collapsed ? (
                          <Tooltip content={item.label} side={tipSide}>
                            {link}
                          </Tooltip>
                        ) : (
                          link
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
      {footer || onToggleCollapsed ? (
        <div className={cn("flex shrink-0 flex-col items-start gap-2 pt-1 pb-3", collapsed ? "items-center px-2.5" : "ps-5 pe-3")}>
          {footer}
          {onToggleCollapsed ? (
            <Tooltip content={toggleLabel} side={tipSide}>
              <Button variant="ghost" size="icon-sm" onClick={onToggleCollapsed} aria-expanded={!collapsed} aria-label={toggleLabel} className="text-aeblack-600 hover:bg-white hover:text-aegold-700">
                {collapsed ? <PanelLeftOpen className="rtl:rotate-180" /> : <PanelLeftClose className="rtl:rotate-180" />}
              </Button>
            </Tooltip>
          ) : null}
        </div>
      ) : null}
    </nav>
  );
}

/** The platform logo with the workspace (or app) name under it at the start of the shell header; only the mark when the rail is folded. */
export function SideNavBrand({ subtitle, collapsed, onHome, homeHref = "/", textClassName }: { subtitle?: ReactNode; collapsed?: boolean; onHome?: () => void; homeHref?: string; textClassName?: string }) {
  const locale = useDocumentLocale();
  return (
    <a
      href={homeHref}
      onClick={(e) => {
        if (onHome) {
          e.preventDefault();
          onHome();
        }
      }}
      aria-label={collapsed ? microcopy("brandName", locale) : undefined}
      className={cn("flex min-w-0 items-center gap-3 rounded-lg outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring", collapsed && "justify-center")}
    >
      <BrandMark size={collapsed ? 34 : 40} />
      {collapsed ? null : (
        <span className={cn("flex min-w-0 flex-col gap-1 leading-tight", textClassName)}>
          <BrandWordmark size={17} />
          {subtitle ? <span className="truncate text-xs text-aeblack-600">{subtitle}</span> : null}
        </span>
      )}
    </a>
  );
}

// ---------------------------------------------------------------- Top bar
/** The header row beside the brand: context on the start side (breadcrumbs or the page title), controls on the end side. */
export function TopBar({ start, children, end, className }: { start?: ReactNode; children?: ReactNode; end?: ReactNode; className?: string }) {
  return (
    <div className={cn("@container/topbar flex h-full min-w-0 items-center gap-2 px-2 sm:px-4", className)}>
      {start}
      <div className="flex min-w-0 flex-1 items-center">{children}</div>
      {end ? (
        <div className="flex shrink-0 items-center gap-1 sm:gap-1.5" role="group" aria-label={microcopy("pageActions")}>
          {end}
        </div>
      ) : null}
    </div>
  );
}

const MobileNavContext = createContext<(() => void) | null>(null);

/** Opens the navigation drawer on phones and tablets; renders nothing on desktop or outside an `AppShell`. */
export function MobileNavButton({ className }: { className?: string }) {
  const open = useContext(MobileNavContext);
  if (!open) return null;
  return (
    <Button variant="ghost" size="icon-sm" className={cn("lg:hidden", className)} onClick={open} aria-label={microcopy("toggleNavigation")}>
      <Menu />
    </Button>
  );
}

// ---------------------------------------------------------------- Page title condensing into the top bar
type TitleState = { owner: string | null; title: ReactNode; condensed: boolean };
let titleState: TitleState = { owner: null, title: null, condensed: false };
const titleListeners = new Set<() => void>();
const setTitleState = (next: Partial<TitleState>) => {
  titleState = { ...titleState, ...next };
  titleListeners.forEach((l) => l());
  if ("title" in next) syncDocumentTitle();
};

let documentAppName = "";
const textOf = (n: ReactNode): string =>
  typeof n === "string" || typeof n === "number"
    ? String(n)
    : Array.isArray(n)
      ? n.map(textOf).join("")
      : isValidElement<{ children?: ReactNode }>(n)
        ? textOf(n.props.children)
        : "";
function syncDocumentTitle() {
  if (typeof document === "undefined") return;
  const page = textOf(titleState.title).replace(/\s+/g, " ").trim();
  const next = page && documentAppName ? `${page} · ${documentAppName}` : page || documentAppName;
  if (next && document.title !== next) document.title = next;
}

/** Names the product in the browser tab (after the current page title) and its meta description, in the reader's language. */
export function useDocumentIdentity(appName: string, description?: string) {
  useEffect(() => {
    documentAppName = appName;
    syncDocumentTitle();
  }, [appName]);
  useEffect(() => {
    if (!description || typeof document === "undefined") return;
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);
  }, [description]);
}
const subscribeTitle = (l: () => void) => {
  titleListeners.add(l);
  return () => void titleListeners.delete(l);
};

/** The current page's title and whether it has scrolled out of view (so the top bar can show it). */
export function usePageTitleState(): { title: ReactNode; condensed: boolean } {
  return useSyncExternalStore(subscribeTitle, () => titleState, () => titleState);
}

/** Shows `fallback` (the breadcrumbs) until the page heading scrolls away, then the page title. */
export function CondensedPageTitle({ fallback, className }: { fallback?: ReactNode; className?: string }) {
  const { title, condensed } = usePageTitleState();
  const showTitle = condensed && title != null;
  return (
    <div className={cn("relative flex min-w-0 flex-1 items-center", className)}>
      <AnimatePresence mode="wait" initial={false}>
        {showTitle ? (
          <motion.p key="title" className="truncate font-heading text-sm font-semibold text-strong" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.18 }}>
            {title}
          </motion.p>
        ) : (
          <motion.div key="fallback" className="min-w-0" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} transition={{ duration: 0.18 }}>
            {fallback}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------- App shell (docked header + side navigation + optional docked panel)
export interface ShellAside {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /** Controls in the panel header, before the close button. */
  actions?: ReactNode;
  children: ReactNode;
  /** Remembers the docked width under this key. */
  storageKey?: string;
}

const ASIDE_MIN = 340;
const ASIDE_MAX = 680;
const clampAside = (w: number) => Math.min(ASIDE_MAX, Math.max(ASIDE_MIN, Math.round(w)));

function DockedAside({ aside }: { aside: ShellAside }) {
  const locale = useDocumentLocale();
  const titleId = useId();
  const key = aside.storageKey ?? "pmo.aside.width";
  const [width, setWidth] = useState(() => {
    try {
      const v = Number(localStorage.getItem(key));
      return v ? clampAside(v) : 420;
    } catch {
      return 420;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, String(width));
    } catch {
      /* storage unavailable */
    }
  }, [key, width]);
  // The panel sits on the end side: dragging toward the page widens it, which is leftward in English and rightward in Arabic.
  const toward = locale === "ar" ? -1 : 1;
  const drag = useRef<{ x: number; w: number } | null>(null);
  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    drag.current = { x: e.clientX, w: width };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (drag.current) setWidth(clampAside(drag.current.w + (drag.current.x - e.clientX) * toward));
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    drag.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 64 : 16;
    if (e.key === "ArrowLeft") setWidth((w) => clampAside(w + step * toward));
    else if (e.key === "ArrowRight") setWidth((w) => clampAside(w - step * toward));
    else if (e.key === "Home") setWidth(ASIDE_MIN);
    else if (e.key === "End") setWidth(ASIDE_MAX);
    else return;
    e.preventDefault();
  };

  return (
    <AnimatePresence initial={false}>
      {aside.open ? (
        <motion.aside
          key="aside"
          data-print="hide"
          aria-labelledby={titleId}
          className="sticky top-[var(--shell-top)] ms-3 flex h-[calc(100dvh-var(--shell-top)-var(--banner-h))] shrink-0 flex-col self-start overflow-hidden rounded-t-2xl bg-white"
          style={{ width }}
          initial={{ opacity: 0, x: 16 * toward }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 16 * toward }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
        >
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label={microcopy("resizePanel", locale)}
            aria-valuemin={ASIDE_MIN}
            aria-valuemax={ASIDE_MAX}
            aria-valuenow={width}
            tabIndex={0}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onKeyDown={onKeyDown}
            className="group/resize absolute inset-y-0 start-0 z-10 flex w-2 cursor-col-resize touch-none justify-center outline-none"
          >
            <span className="h-full w-0.5 bg-transparent transition-colors group-hover/resize:bg-aegold-400 group-focus-visible/resize:bg-aegold-500" />
          </div>
          <div className="flex h-14 shrink-0 items-center gap-2 border-b border-aeblack-100 ps-5 pe-2">
            <span aria-hidden className="grid size-7 shrink-0 place-items-center rounded-md bg-gradient-ai text-white">
              <Sparkles className="size-4" />
            </span>
            <h2 id={titleId} className="min-w-0 flex-1 truncate font-heading text-base font-bold text-aeblack-800">{aside.title}</h2>
            {aside.actions}
            <Button variant="ghost" size="icon-sm" onClick={() => aside.onOpenChange(false)} aria-label={microcopy("closeAssistant", locale)}>
              <X />
            </Button>
          </div>
          <div className="flex min-h-0 flex-1 flex-col">{aside.children}</div>
        </motion.aside>
      ) : null}
    </AnimatePresence>
  );
}

/**
 * The docked frame of the authenticated apps, laid out under a sticky classification banner (`--banner-h`) and above a fixed one.
 * It is the frame of the UAE Design System's own site: the header (the gold rule, the brand in a column as wide as the side
 * navigation, then `header`, then the UAE emblem) and the side navigation sit on Whitely 200 without rules, and the page is a white
 * sheet with 16px top corners beside them; an optional panel docks on the end side as a second sheet.
 */
export function AppShell({
  header,
  brand,
  sidebar,
  mobileSidebar,
  children,
  collapsed = false,
  className,
  mainClassName,
  fullWidth = false,
  aside,
}: {
  /** The rest of the header row after the brand (see `TopBar`). */
  header: ReactNode;
  /** The emblem and product name at the start of the header row (see `SideNavBrand`). */
  brand?: ReactNode;
  sidebar: ReactNode;
  /** Rendered inside the mobile drawer instead of `sidebar` (e.g. an always-expanded nav when the desktop one is collapsed). */
  mobileSidebar?: ReactNode;
  children: ReactNode;
  /** @deprecated The sidebar width comes from `--sidebar-w`. */
  sidebarWidth?: string;
  collapsed?: boolean;
  className?: string;
  /** Replaces the default page padding of `<main>` (chat-style surfaces render edge to edge and manage their own scrolling). */
  mainClassName?: string;
  /** Lets the page span the whole width instead of the reading column. */
  fullWidth?: boolean;
  /** A panel docked on the end side on wide screens (the assistant); a side sheet on narrower ones. */
  aside?: ShellAside;
}) {
  const locale = useDocumentLocale();
  const [navOpen, setNavOpen] = useState(false);
  const docked = useMediaQuery("(min-width: 1280px)");
  const openNav = useCallback(() => setNavOpen(true), []);
  return (
    <MobileNavContext.Provider value={openNav}>
      <div className={cn("flex min-h-[calc(100dvh-var(--banner-h))] flex-col", className)}>
        <header data-print="hide" className="sticky top-[var(--banner-h)] z-30 flex shrink-0 flex-col bg-canvas">
          <span aria-hidden className="gov-strip block h-[var(--gov-strip-h)] shrink-0" />
          <div className="flex h-[var(--topbar-h)] min-w-0">
            <div
              className={cn(
                "flex min-w-0 shrink-0 items-center gap-1 ps-2 sm:ps-3 lg:transition-[width] lg:duration-300 lg:ease-out-expo",
                collapsed ? "lg:w-[var(--sidebar-w-collapsed)] lg:justify-center lg:ps-3" : "lg:w-[max(var(--sidebar-w),min(24rem,32vw))] lg:ps-6",
              )}
            >
              <MobileNavButton />
              {brand}
            </div>
            <div className="min-w-0 flex-1">{header}</div>
            <div className="hidden shrink-0 items-center pe-5 ps-2 xl:flex">
              <GovEmblem className="h-12" />
            </div>
          </div>
        </header>
        <div className="flex min-w-0 flex-1">
          <aside
            data-print="hide"
            className={cn(
              "sticky top-[var(--shell-top)] hidden h-[calc(100dvh-var(--shell-top)-var(--banner-h))] shrink-0 self-start bg-canvas transition-[width] duration-300 ease-out-expo lg:block",
              collapsed ? "w-[var(--sidebar-w-collapsed)]" : "w-[var(--sidebar-w)]",
            )}
          >
            <div className="h-full overflow-hidden">{sidebar}</div>
          </aside>
          <div className="flex min-w-0 flex-1 flex-col bg-white lg:rounded-t-2xl">
            <main id="main" tabIndex={-1} className={cn("@container/main mx-auto w-full min-w-0 flex-1 outline-none", fullWidth ? "max-w-none" : "max-w-[1600px]", mainClassName ?? "px-4 pt-6 pb-12 sm:px-6 lg:px-10 lg:pt-8")}>
              {children}
            </main>
          </div>
          {aside && docked ? <DockedAside aside={aside} /> : null}
        </div>
      </div>
      {aside && !docked ? (
        <Sheet open={aside.open} onOpenChange={aside.onOpenChange}>
          <SheetContent side="end" width="max-w-lg" emphasis="secondary" className="gap-0" aria-describedby={undefined}>
            <SheetHeader className="pb-3">
              <SheetTitle>{aside.title}</SheetTitle>
            </SheetHeader>
            <div className="flex min-h-0 flex-1 flex-col">{aside.children}</div>
          </SheetContent>
        </Sheet>
      ) : null}
      <MobileDrawer side="start" open={navOpen} onOpenChange={setNavOpen}>
        <MobileDrawerContent aria-describedby={undefined} className="!bg-canvas">
          <MobileDrawerTitle className="sr-only">{microcopy("mainMenu", locale)}</MobileDrawerTitle>
          <div
            className="min-h-0 flex-1"
            onClickCapture={(e) => {
              if ((e.target as HTMLElement).closest("a[href]")) setNavOpen(false);
            }}
          >
            {mobileSidebar ?? sidebar}
          </div>
        </MobileDrawerContent>
      </MobileDrawer>
    </MobileNavContext.Provider>
  );
}

// ---------------------------------------------------------------- Page header
export interface Crumb {
  label: ReactNode;
  href?: string;
  onClick?: () => void;
}

export function PageHeader({ title, description, crumbs, actions, eyebrow, className, meta }: { title: ReactNode; description?: ReactNode; crumbs?: Crumb[]; actions?: ReactNode; eyebrow?: ReactNode; className?: string; meta?: ReactNode }) {
  const owner = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    setTitleState({ owner, title });
  });
  useEffect(() => {
    const el = headingRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (titleState.owner === owner) setTitleState({ condensed: !entry.isIntersecting && entry.boundingClientRect.top < 80 });
      },
      { rootMargin: "-80px 0px 0px 0px" },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (titleState.owner === owner) setTitleState({ owner: null, title: null, condensed: false });
    };
  }, [owner]);
  return (
    <div className={cn("mb-8 flex flex-col gap-3", className)}>
      {crumbs && crumbs.length > 0 ? (
        <nav aria-label={microcopy("breadcrumb")} className="flex items-center gap-1 text-sm text-aegold-700">
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 ? <ChevronRight className="size-3.5 rtl:rotate-180" aria-hidden /> : null}
              {c.href || c.onClick ? (
                <a
                  href={c.href ?? "#"}
                  onClick={(e) => {
                    if (c.onClick) {
                      e.preventDefault();
                      c.onClick();
                    }
                  }}
                  className="rounded-sm font-medium text-aegold-700 underline decoration-1 underline-offset-2 transition-colors hover:text-aegold-800 hover:decoration-2"
                >
                  {c.label}
                </a>
              ) : (
                <span aria-current="page" className="font-semibold text-aeblack-800">{c.label}</span>
              )}
            </span>
          ))}
        </nav>
      ) : null}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          {eyebrow ? <p className="mb-2 text-base font-semibold text-aegold-600">{eyebrow}</p> : null}
          <h1 ref={headingRef} className="text-[2rem] leading-tight font-extrabold text-aeblack-800 @5xl/main:text-h3">
            {title}
          </h1>
          {description ? <p className="mt-3 max-w-3xl text-lg leading-relaxed text-aeblack-600">{description}</p> : null}
          {meta ? <div className="mt-3 flex flex-wrap items-center gap-2">{meta}</div> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Footer
export interface GovFooterColumn {
  title: ReactNode;
  /** Links (anchors or router links); the footer styles them. */
  items: ReactNode[];
}

/**
 * The UAE Design System 3.0 footer (`aegov-footer`): AE Black 50 under the 8px gold rule, the platform's logo and owner with any link
 * columns, then the bottom row (`footer-bottom`) above an AE Black 100 rule with `children` (the copyright line) and flat `links`.
 */
export function GovFooter({ children, links, columns, about, className }: {
  children: ReactNode;
  links?: Array<{ label: ReactNode; href: string }>;
  columns?: GovFooterColumn[];
  /** A line or two under the logo (who runs the platform). */
  about?: ReactNode;
  className?: string;
}) {
  const linkCls = "[&_a]:rounded-sm [&_a]:text-aeblack-700 [&_a]:transition-colors [&_a]:duration-300 [&_a:hover]:text-aegold-700 [&_a:hover]:underline [&_a]:underline-offset-2";
  return (
    <footer data-print="hide" className={cn("relative mt-auto bg-aeblack-50 text-sm", className)}>
      <span aria-hidden className="gov-strip block h-2" />
      {columns?.length || about ? (
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-10 sm:px-6 md:grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))] lg:px-8">
          <div className="flex flex-col gap-3">
            <span className="flex items-center gap-3">
              <BrandMark size={40} />
              <BrandWordmark size={18} />
            </span>
            {about ? <div className="max-w-sm text-aeblack-600">{about}</div> : null}
          </div>
          {columns?.map((col, i) => (
            <nav key={i} aria-label={typeof col.title === "string" ? col.title : undefined} className="flex flex-col gap-3">
              <p className="font-heading text-base font-bold text-aegold-700">{col.title}</p>
              <ul className={cn("flex flex-col gap-2.5", linkCls)}>
                {col.items.map((item, j) => <li key={j}>{item}</li>)}
              </ul>
            </nav>
          ))}
        </div>
      ) : null}
      <div className={cn("border-aeblack-100", (columns?.length || about) && "border-t")}>
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-5 text-xs text-aeblack-600 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            {columns?.length || about ? null : <BrandMark size={28} />}
            <span>{children}</span>
          </div>
          {links ? (
            <ul className={cn("flex flex-wrap gap-4", linkCls)}>
              {links.map((l, i) => (
                <li key={i}>
                  <a href={l.href}>{l.label}</a>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </footer>
  );
}

// ---------------------------------------------------------------- Collapsible section
/** A page section that shows its heading only until opened, so long pages read as a list of headings. */
export function CollapsibleSection({ title, meta, actions, defaultOpen = false, open: controlled, onOpenChange, children, className, contentClassName }: {
  title: ReactNode; meta?: ReactNode; actions?: ReactNode; defaultOpen?: boolean; open?: boolean; onOpenChange?: (open: boolean) => void; children: ReactNode; className?: string; contentClassName?: string;
}) {
  const [uncontrolled, setUncontrolled] = useState(defaultOpen);
  const open = controlled ?? uncontrolled;
  const toggle = () => { const next = !open; setUncontrolled(next); onOpenChange?.(next); };
  return (
    <section className={cn("glass-panel min-w-0 rounded-xl", className)}>
      <div className="flex items-center gap-3 px-5 py-3">
        <button type="button" aria-expanded={open} onClick={toggle} className="flex min-w-0 flex-1 items-center gap-2 rounded-md text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <ChevronDown className={cn("size-4 shrink-0 text-subtle transition-transform duration-200", !open && "-rotate-90 rtl:rotate-90")} />
          <span className="truncate font-heading text-base font-semibold text-strong">{title}</span>
          {meta ? <span className="shrink-0 text-xs text-muted-foreground">{meta}</span> : null}
        </button>
        {actions}
      </div>
      {open ? <div className={cn("border-t border-hairline p-5 animate-fade-in", contentClassName)}>{children}</div> : null}
    </section>
  );
}

/** A small info mark whose tooltip carries the explanation that used to sit under a heading. */
export function InfoHint({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <Tooltip content={children}>
      <button type="button" aria-label={typeof children === "string" ? children : undefined} className={cn("inline-grid size-5 shrink-0 place-items-center rounded-full text-sand-500 hover:text-aegold-700", className)}>
        <Info className="size-3.5" />
      </button>
    </Tooltip>
  );
}
